import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";

import { Lock } from "@assets/svg/Lock";
import { TextInput } from "@components/Input";
import { ChildSsnSkipModal } from "@components/Modals/ChildSsnSkipModal";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { TextHighlighter } from "@components/TextHighlighter";
import { BodySmall, Caption, H2 } from "@components/Typography";
import { AccountType, BeneficiaryType, DateFormat, Flow, GoalType, Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useLocation, useNavigate } from "@entry/navigation";
import {
  useAddBeneficiary,
  useAddGoal,
  useErrorBanner,
  useGoalFlow,
  useModal,
  useOnboarding,
  usePersistSidecarGoal,
  useTheme,
  useUpdateBeneficiary,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { constants } from "@layout";
import { formatSsnNumber, getHarvestAddGoalVariables, isFiveTwoNineAccountType } from "@utils";
import { validationSchema } from "./content";
import { useStyles } from "./styles";
import { BeneficiarySsnFormValues, BeneficiarySsnRouteFlow } from "./types";

export const BeneficiarySsnRoute = () => {
  const styles = useStyles();
  const { theme } = useTheme();
  const { show } = useModal();

  const navigate = useNavigate();
  const location = useLocation();
  const { resetToNext } = useOnboarding();

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<BeneficiarySsnFormValues>({
    defaultValues: {
      ssn: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const {
    goalProps: {
      beneficiaryId,
      flow,
      accountType,
      firstName,
      lastName,
      dateOfBirth,
      beneficiaryType = BeneficiaryType.Child,
      goalName,
      goalType,
      harvestStandardDeduction,
      harvestLongTermCapitalGains,
      harvestRecommendedFloorUsd,
    },
    setGoalProps,
  } = useGoalFlow();

  const [updateBeneficiary, { loading: updateBeneficiaryLoading }] = useUpdateBeneficiary();
  const [addBeneficiary, { loading: addBeneficiaryLoading }] = useAddBeneficiary();
  const [addGoal, { loading: addGoalLoading }] = useAddGoal();
  const { persistSidecarGoal, loading: sidecarLoading } = usePersistSidecarGoal();
  const loading =
    updateBeneficiaryLoading || addBeneficiaryLoading || addGoalLoading || sidecarLoading;

  const { showErrorBanner } = useErrorBanner();
  const { beneficiaries, user } = useUser();

  const beneficiary = beneficiaries?.find((b) => b?.id === beneficiaryId);
  const childName =
    beneficiary?.firstName ||
    firstName ||
    i18n.t(Translations.BeneficiarySsnRouteTitleDefaultName) ||
    "";
  const hasCustodialRothIra = Boolean(
    beneficiary?.goals?.some((g) =>
      g?.accounts?.some((a) => a?.type === AccountType.CustodialRothIra),
    ),
  );

  const ssnController = register("ssn");

  const onChangeText = (value: string) => {
    const formattedValue = formatSsnNumber(value);

    setValue("ssn", formattedValue, constants.form);
  };

  const collectBeneficiarySsnFromHome =
    (location.state as { beneficiaryId?: string; flow?: BeneficiarySsnRouteFlow })?.flow ===
    BeneficiarySsnRouteFlow.HomeBeneficiarySsnCollection
      ? (location.state as { beneficiaryId?: string; flow?: BeneficiarySsnRouteFlow }).beneficiaryId
      : undefined;

  const isHomeSsnCollection = Boolean(collectBeneficiarySsnFromHome);

  // Skipping routes to ChildDeposit, which creates the goal without any sidecar
  // fields — never offer it for a sidecar goal.
  const showSkipSecondaryCta =
    !isHomeSsnCollection &&
    isFiveTwoNineAccountType(accountType) &&
    goalType !== GoalType.TrumpAccountSidecar;

  const handleSubmit = async ({ ssn }: BeneficiarySsnFormValues) => {
    const completeCustodialFlow = async () => {
      if (flow === Flow.Onboarding || !flow) {
        await resetToNext();
        return;
      }

      if (flow === Flow.AddGoal || flow === Flow.Accounts) {
        navigate(Routes.Accounts, { replace: true });
        return;
      }

      if (flow === Flow.AcceptSharedGoal) {
        navigate(Routes.Dashboard, { replace: true });
        return;
      }

      navigate(Routes.Dashboard, { replace: true });
    };

    const routeThroughMaritalStatusIfNeeded = async () => {
      if (user?.maritalStatus) {
        await completeCustodialFlow();
      } else {
        navigate(Routes.MaritalStatus, { replace: true });
      }
    };

    if (collectBeneficiarySsnFromHome && ssn) {
      try {
        await updateBeneficiary({
          variables: {
            beneficiaryId: collectBeneficiarySsnFromHome,
            taxId: ssn,
          },
        });

        navigate(-1);
      } catch (e: any) {
        const message = e.graphQLErrors?.[0]?.message;
        showErrorBanner(message);
      }

      return;
    }

    if (goalType === GoalType.TrumpAccountSidecar) {
      try {
        if (beneficiaryId && ssn) {
          await updateBeneficiary({ variables: { beneficiaryId, taxId: ssn } });
        }

        setGoalProps({ ssn });

        await persistSidecarGoal({ ssn });

        completeCustodialFlow();
      } catch (e: any) {
        const message = e.graphQLErrors?.[0]?.message;
        showErrorBanner(message);
      }

      return;
    }

    if (accountType === AccountType.CustodialRothIra) {
      try {
        if (beneficiaryId && ssn) {
          await updateBeneficiary({
            variables: {
              beneficiaryId,
              taxId: ssn,
            },
          });

          setGoalProps({ ssn });

          if (!hasCustodialRothIra) {
            await addGoal({
              variables: {
                accountType,
                beneficiaryId,
                goalName,
                goalType,
                ...getHarvestAddGoalVariables({
                  accountType,
                  harvestStandardDeduction,
                  harvestLongTermCapitalGains,
                  harvestRecommendedFloorUsd,
                }),
              },
            });
          }

          await routeThroughMaritalStatusIfNeeded();
          return;
        }

        if (!firstName || !lastName || !dateOfBirth || !dayjs(dateOfBirth).isValid() || !ssn) {
          return showErrorBanner();
        }

        setGoalProps({ ssn });

        const { data } = await addBeneficiary({
          variables: {
            firstName,
            lastName,
            dateOfBirth: dayjs(dateOfBirth).format(DateFormat.User),
            type: beneficiaryType,
            taxId: ssn,
          },
        });

        const id = data?.addBeneficiary?.id;
        if (!id) return showErrorBanner();

        await addGoal({
          variables: {
            accountType,
            beneficiaryId: id,
            goalName,
            goalType,
            ...getHarvestAddGoalVariables({
              accountType,
              harvestStandardDeduction,
              harvestLongTermCapitalGains,
              harvestRecommendedFloorUsd,
            }),
          },
        });

        await routeThroughMaritalStatusIfNeeded();
      } catch (e: any) {
        const message = e.graphQLErrors?.[0]?.message;
        showErrorBanner(message);
      }

      return;
    }

    if (beneficiaryId && ssn) {
      try {
        await updateBeneficiary({
          variables: {
            beneficiaryId,
            taxId: ssn,
          },
        });

        setGoalProps({ ssn });

        navigate(Routes.GoalDeposit, { replace: true });
      } catch (e: any) {
        const message = e.graphQLErrors?.[0]?.message;
        showErrorBanner(message);
      }
    } else {
      setGoalProps({ ssn });
      if (flow === Flow.AddGoal || flow === Flow.AcceptSharedGoal) {
        navigate(Routes.GoalDeposit, { replace: true });
      } else {
        navigate(Routes.ChildDeposit, { replace: true });
      }
    }
  };

  const props: OnboardingLayoutProps = {
    cta: {
      primary: {
        title: i18n.t(Translations.BeneficiarySsnRoutePrimaryCTA, { name: childName }),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
        loading,
      },
      ...(showSkipSecondaryCta
        ? {
            secondary: {
              title: i18n.t(Translations.BeneficiarySsnRouteSkip),
              onClick: () => show(<ChildSsnSkipModal beneficiaryFirstName={childName} />),
            },
          }
        : {}),
    },
    footerStyle: styles.footer,
    footer: (
      <div style={styles.footerContent}>
        <Lock size={16} fill={theme.text.tertiary} />
        <Caption style={{ ...styles.footerText, color: theme.text.secondary }}>
          {i18n.t(Translations.BeneficiarySsnRouteFooter)}
        </Caption>
      </div>
    ),
  };

  return (
    <OnboardingLayout {...props}>
      <Spacing size={SpacingSizes.M} />
      <Spacing size={SpacingSizes.M} />
      <Caption style={styles.eyebrow}>
        {i18n.t(Translations.BeneficiarySsnRouteHeader, { name: childName })}
      </Caption>
      <Spacing size={SpacingSizes.XS} />
      <TextHighlighter
        text={i18n.t(Translations.BeneficiarySsnRouteTitle, { name: childName })}
        words={[{ text: childName }]}
        TypographyComponent={H2}
        highlightColor={theme.text.highlight}
      />
      <Spacing size={SpacingSizes.M} />
      <BodySmall>{i18n.t(Translations.BeneficiarySsnRouteSubtitle, { name: childName })}</BodySmall>
      <Spacing size={SpacingSizes.M} />
      <TextInput
        {...ssnController}
        label={i18n.t(Translations.BeneficiarySsnRouteInputLabel, { name: childName })}
        placeholder={i18n.t(Translations.BeneficiarySsnRouteInputPlaceholder)}
        value={watch("ssn")}
        onChangeText={onChangeText}
        error={errors.ssn?.message}
        inputLeftIcon={<Lock size={16} fill={theme.text.tertiary} />}
      />
      <Spacing size={SpacingSizes.S} />
      <BodySmall style={styles.laterHint}>
        {i18n.t(Translations.BeneficiarySsnRouteLaterHint)}
      </BodySmall>
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
